import { zodResolver } from '@hookform/resolvers/zod'
import { Grid, Paper, Stack, TablePagination } from '@mui/material'
import Breadcrumbs from 'components/Breadcrumbs'
import { Form } from 'components/Form'
import Label from 'components/Label'
import { usePagination } from 'hooks/pagination.hook'
import Layout from 'layouts/Default'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import teenagerService from 'services/teenager.service'
import { PageableProps } from 'types/pageable.type'
import {
  SummaryTeenagerType,
  teenagerFilterInit,
  teenagerFilterSchema,
  TeenagerFilterType,
} from 'types/search-teenager.type'
import { useAuthContext } from 'contexts/auth.context'
import FormGroup from './components/FormGroup'
import TableData from './components/TableData'
import { StyledBoxContainerTitle as ContainerTitle } from './styles'
import { SystemMessages } from 'utils/enums/system-message.enum'
import { doNotHasText } from 'utils/validate.util'
import { useBackdrop } from 'contexts/backdrop.context'

const SearchTeenager: React.FC = () => {
  const [showMessage, setShowMessage] = useState(false)

  const methods = useForm<TeenagerFilterType>({
    resolver: zodResolver(teenagerFilterSchema),
    defaultValues: teenagerFilterInit,
  })

  const { getValues, watch } = methods

  const { handleHasPermission } = useAuthContext()
  const { handleBackdropOpen, handleBackdropClose } = useBackdrop()
  const {
    page,
    elements: { totalOfElements, handleTotalOfElements },
    rows: { rowsPerPage, rowsOptions },
    handleNewPage,
    handleRowsPerPage,
  } = usePagination()

  const formNotValid =
    doNotHasText(watch('dataNascimento')) &&
    doNotHasText(watch('documento')) &&
    doNotHasText(watch('nomeMae')) &&
    doNotHasText(watch('nomePai')) &&
    doNotHasText(watch('representanteLegal')) &&
    doNotHasText(watch('nomeSocial')) &&
    doNotHasText(watch('nome'))

  const [data, setData] = useState<SummaryTeenagerType[] | undefined>(undefined)

  const getTeenagers = async (filters: TeenagerFilterType, pageable: PageableProps) => {
    handleBackdropOpen()
    await teenagerService
      .getByFilters(filters, pageable)
      .then((response) => {
        setShowMessage(false)
        setData(response.data.content)
        handleTotalOfElements(response.data.totalElements)
      })
      .finally(() => handleBackdropClose())
  }

  const handleSubmitForm = async (filters: TeenagerFilterType): Promise<void> => {
    if (!filters) return
    if (formNotValid) {
      setShowMessage(true)
      return
    }
    handleNewPage(null, 0)
    const pageable: PageableProps = {
      page: 0,
      pageSize: rowsPerPage,
    }
    await getTeenagers(filters, pageable)
  }

  const handleResetTable = (): void => {
    setShowMessage(false)
    setData(undefined)
  }

  const handlePage = async (newPage: number, newRowsPerPage?: any) => {
    handleNewPage(null, newPage)
    if (newRowsPerPage) {
      handleRowsPerPage(newRowsPerPage)
    }
    if (data && data.length > 0) {
      if (!formNotValid) {
        const pageable: PageableProps = {
          page: newPage,
          pageSize: newRowsPerPage?.target?.value || rowsPerPage,
        }
        await getTeenagers(getValues(), pageable)
      } else {
        setShowMessage(false)
      }
    }
  }

  return (
    <Layout>
      <Grid container item xs={12} sx={{ mb: 2 }}>
        <Breadcrumbs>
          <Label text='Consultar Adolescente' bold v='body1' />
        </Breadcrumbs>
      </Grid>

      <Grid container direction='column' spacing={4}>
        <Grid item xs={12}>
          <ContainerTitle>
            <Label text='INFORMAÇÕES DO ADOLESCENTE' v='h6' bold />
          </ContainerTitle>
        </Grid>

        <Grid item xs={12} sx={{ pb: 2 }}>
          <Form methods={methods} onSubmit={methods.handleSubmit(handleSubmitForm)}>
            <FormGroup onHasPermission={handleHasPermission} onResetTable={handleResetTable} />
          </Form>
        </Grid>

        {showMessage ? (
          <Grid item xs={12}>
            <Label bold text={SystemMessages.MESSAGE_020} sx={{ color: 'error.main' }} />
          </Grid>
        ) : (
          data && (
            <Grid item xs={12}>
              {data.length > 0 ? (
                <Stack direction='column' spacing={2}>
                  <Label text='Resultados da busca:' />
                  <Paper variant='outlined'>
                    <TableData data={data} onHasPermission={handleHasPermission} />
                    <TablePagination
                      labelRowsPerPage='Linhas por página:'
                      labelDisplayedRows={({ from, to, count }) =>
                        `Linhas ${from}-${to} de um total de ${count} itens.`
                      }
                      rowsPerPageOptions={rowsOptions}
                      count={totalOfElements}
                      rowsPerPage={rowsPerPage}
                      onPageChange={(e, p) => handlePage(p)}
                      page={page}
                      component='div'
                      onRowsPerPageChange={(e) => handlePage(0, e)}
                    />
                  </Paper>
                </Stack>
              ) : (
                <Stack direction='row' justifyContent='center' spacing={2}>
                  <Label text='Nenhum resultado encontrado...' bold />
                </Stack>
              )}
            </Grid>
          )
        )}
      </Grid>
    </Layout>
  )
}

export default SearchTeenager
